/**
 * Safe JSON utilities to prevent circular structure errors,
 * specifically handling Firebase Firestore internals (Y2, Ka) and circular object references.
 */

// Detect Firebase SDK internal classes or objects with circular references
export const isFirestoreInternal = (val: any): boolean => {
  if (!val || typeof val !== 'object') return false;
  
  const cName = val.constructor?.name;
  if (
    cName === 'Y2' ||
    cName === 'Ka' ||
    cName === 'Z2' ||
    cName === 'Yc' ||
    cName === 'Firestore' ||
    cName === 'LoadBalancer' ||
    cName === 'DocumentReference' ||
    cName === 'CollectionReference'
  ) {
    return true;
  }

  // Check for Firestore client delegates or private SDK properties
  if (
    val._firestoreClient ||
    val._delegate ||
    (val.firestore && typeof val.firestore === 'object' && val.firestore._delegate)
  ) {
    return true;
  }

  return false;
};

/**
 * Deeply sanitizes data by stripping circular references, undefined fields,
 * and converting complex SDK / Date / Firestore objects into clean, plain serializable values.
 */
export const safeSanitize = (data: any, seen = new WeakSet()): any => {
  if (data === null || data === undefined) return data;
  if (typeof data !== 'object') return data;

  // Prevent circular loops
  if (seen.has(data)) {
    return '[Circular]';
  }
  seen.add(data);

  // Handle Date objects
  if (data instanceof Date) {
    return data.toISOString();
  }

  // Handle Firebase Firestore Timestamp objects
  if (typeof data.toDate === 'function') {
    try {
      return data.toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }

  // Exclude Firestore internal objects directly
  if (isFirestoreInternal(data)) {
    const cName = data.constructor?.name || 'FirestoreInternal';
    return `[${cName}]`;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data
      .filter(item => typeof item !== 'function')
      .map(item => safeSanitize(item, seen));
  }

  // Handle plain objects
  const sanitized: Record<string, any> = {};
  const keys = Object.keys(data);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = data[key];

    // Skip undefined and functions
    if (value === undefined || typeof value === 'function') continue;

    if (value && typeof value === 'object') {
      if (isFirestoreInternal(value)) {
        sanitized[key] = `[${value.constructor?.name || 'FirestoreInternal'}]`;
      } else {
        sanitized[key] = safeSanitize(value, seen);
      }
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Safely stringifies any value to JSON without ever throwing
 * "TypeError: Converting circular structure to JSON".
 */
export const safeStringify = (value: any, replacer?: any, space?: string | number): string => {
  try {
    return JSON.stringify(value, replacer, space);
  } catch (err: any) {
    // If native JSON.stringify throws a circular structure error, fallback to safe cycle-breaking serializer
    const seen = new WeakSet();
    try {
      return JSON.stringify(
        value,
        function (this: any, key: string, val: any) {
          if (typeof val === 'object' && val !== null) {
            if (isFirestoreInternal(val)) {
              return `[${val.constructor?.name || 'FirestoreInternal'}]`;
            }
            if (seen.has(val)) {
              return '[Circular]';
            }
            seen.add(val);
          }
          if (typeof replacer === 'function') {
            return replacer.call(this, key, val);
          }
          return val;
        },
        space
      );
    } catch {
      // Last-ditch recovery: sanitize first then stringify
      try {
        const cleaned = safeSanitize(value);
        return JSON.stringify(cleaned, null, space);
      } catch {
        return '"{}"';
      }
    }
  }
};

/**
 * Installs a transparent safety wrapper on global JSON.stringify to guarantee
 * that no library, React DevTools, iframe message listener, or runtime code
 * crashes with "TypeError: Converting circular structure to JSON".
 */
export const installSafeJsonStringify = (): void => {
  if (typeof window === 'undefined' || (window as any).__SAFE_JSON_STRINGIFY_INSTALLED__) {
    return;
  }

  (window as any).__SAFE_JSON_STRINGIFY_INSTALLED__ = true;
  const originalStringify = JSON.stringify;

  JSON.stringify = function (value: any, replacer?: any, space?: any): string {
    try {
      return originalStringify(value, replacer, space);
    } catch (err: any) {
      if (
        err &&
        typeof err.message === 'string' &&
        (err.message.includes('circular') || err.message.includes('cyclic') || err.message.includes('Converting circular structure'))
      ) {
        const seen = new WeakSet();
        try {
          return originalStringify(
            value,
            function (this: any, key: string, val: any) {
              if (typeof val === 'object' && val !== null) {
                if (isFirestoreInternal(val)) {
                  return `[${val.constructor?.name || 'FirestoreInternal'}]`;
                }
                if (seen.has(val)) {
                  return '[Circular]';
                }
                seen.add(val);
              }
              if (typeof replacer === 'function') {
                return replacer.call(this, key, val);
              }
              return val;
            },
            space
          );
        } catch {
          try {
            return originalStringify(safeSanitize(value), null, space);
          } catch {
            return '{}';
          }
        }
      }
      throw err;
    };
  };
};
